import { DateTime } from "luxon";
import { environment } from "@environments/environment";

const LOG_LEVEL = {
    none: 0,
    trace: 1,
    debug: 2,
    info: 3,
    warn: 4,
    error: 5,
};

type LogLevel = keyof typeof LOG_LEVEL;

type LoggerConfig = {
    level: LogLevel;
};

class Logger {
    level: LogLevel = "none";

    constructor() {
        const loggerConfig = (environment as any)?.logger as LoggerConfig;
        if (loggerConfig?.level) {
            const levels = Object.keys(LOG_LEVEL) as LogLevel[];
            this.level = levels.find(e => e === loggerConfig.level) ?? "none";
        }
    }

    private getCurrentTime(): string {
        const now = DateTime.now();
        return now.toFormat("HH:mm:ss.SSS");
    }

    trace(...message: any[]) {
        if (this.hasSufficientLogLevel("trace")) console.trace(`${this.getCurrentTime()} - [TRACE] 🔎: `, ...message);
    }

    debug(...message: any[]) {
        if (this.hasSufficientLogLevel("debug")) console.debug(`${this.getCurrentTime()} - [DEBUG] 🐞: `, ...message);
    }

    info(...message: any[]) {
        if (this.hasSufficientLogLevel("info")) console.log(`${this.getCurrentTime()} - [INFO] 📝: `, ...message);
    }

    warn(...message: any[]) {
        if (this.hasSufficientLogLevel("warn")) console.warn(`${this.getCurrentTime()} - [WARN] ⚠️: `, ...message);
    }

    error(...message: any[]) {
        if (this.hasSufficientLogLevel("error")) console.error(`${this.getCurrentTime()} - [ERROR] 🚨: `, ...message);
    }

    hasSufficientLogLevel(level: keyof typeof LOG_LEVEL) {
        return LOG_LEVEL[level as keyof typeof LOG_LEVEL] >= LOG_LEVEL[this.level];
    }
}

export const logger: Logger | undefined = (environment as any)?.logger
    ? new Logger()
    : undefined;
